import type { ApiFailure, ApiSuccess, ErrorCode } from "@jipraks/shared";

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | "NETWORK_ERROR",
    message: string,
    readonly requestId?: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

interface JsonResponse<T> {
  ok: boolean;
  status: number;
  json(): Promise<T>;
}

/** Unwraps the `{ success, data | error }` envelope; failures become an ApiRequestError. */
export async function unwrap<T>(
  request: Promise<JsonResponse<ApiSuccess<T> | ApiFailure>>,
): Promise<T> {
  let response: JsonResponse<ApiSuccess<T> | ApiFailure>;
  try {
    response = await request;
  } catch (error) {
    console.warn("API request failed", error);
    throw new ApiRequestError(0, "NETWORK_ERROR", "Network error");
  }
  const body = await response.json();
  if (body.success) return body.data;
  throw new ApiRequestError(response.status, body.error.code, body.error.message, body.error.requestId, body.error.details);
}

export function isNotFound(error: unknown): boolean {
  return error instanceof ApiRequestError && error.status === 404;
}

/** Zod issues from a VALIDATION_ERROR, keyed by the first path segment (the form field). */
export function fieldErrors(error: unknown): Record<string, string> {
  if (!(error instanceof ApiRequestError) || !Array.isArray(error.details)) return {};
  const errors: Record<string, string> = {};
  for (const issue of error.details as Array<{ path?: unknown[]; message?: string }>) {
    const field = String(issue.path?.[0] ?? "");
    if (field && issue.message && !errors[field]) errors[field] = issue.message;
  }
  return errors;
}
